/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EntityType, ExternalActionStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ExternalActionProps {
  id: string;
  entityId: string;
  status: ExternalActionStatus;
  entityType: EntityType;
  actionTypeId: string;
  actionBodyJson: string;
  completedById: string | null;
  dateCompleted: Date | null;
  notes: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ExternalActionRecord extends Record<ExternalActionProps>({
  id: null as any,
  entityId: null as any,
  status: null as any,
  entityType: null as any,
  actionTypeId: null as any,
  actionBodyJson: null as any,
  completedById: null as any,
  dateCompleted: null as any,
  notes: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    entityId: string;
    status: ExternalActionStatus;
    entityType: EntityType;
    actionTypeId: string;
    actionBodyJson: string;
    completedById: string | null;
    dateCompleted: string | number | Date | null;
    notes: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ExternalActionRecord {
    if (json == null) {
      throw new Error('ExternalActionRecord.fromJS: parameter cannot be null');
    }
    return new ExternalActionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ExternalActionJson.id',
      ),
      entityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
        'ExternalActionJson.entityId',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', ExternalActionStatus.Unknown),
        'ExternalActionJson.status',
      ),
      entityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
        'ExternalActionJson.entityType',
      ),
      actionTypeId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'actionTypeId', '00000000-0000-0000-0000-000000000000'),
        'ExternalActionJson.actionTypeId',
      ),
      actionBodyJson: getFromOrThrow(
        json,
        (j) => j.actionBodyJson,
        'ExternalActionJson.actionBodyJson',
      ),
      completedById: json.completedById,
      dateCompleted: json.dateCompleted != null ? new Date(json.dateCompleted) : null,
      notes: json.notes,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ExternalActionJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    entityId: string;
    status: ExternalActionStatus;
    entityType: EntityType;
    actionTypeId: string;
    actionBodyJson: string;
    completedById: string | null;
    dateCompleted: string | number | Date | null;
    notes: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ExternalActionRecord, Error[]] {
    if (json == null) {
      throw new Error('ExternalActionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ExternalActionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ExternalActionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
            'ExternalActionJson.entityId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', ExternalActionStatus.Unknown),
            'ExternalActionJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
            'ExternalActionJson.entityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      actionTypeId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'actionTypeId', '00000000-0000-0000-0000-000000000000'),
            'ExternalActionJson.actionTypeId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      actionBodyJson: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.actionBodyJson, 'ExternalActionJson.actionBodyJson'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      completedById: tryOrDefault(
        () => json.completedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateCompleted: tryOrDefault(
        () => (json.dateCompleted != null ? new Date(json.dateCompleted) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ExternalActionJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ExternalActionProps) {
    super(props);
    if (props == null) {
      throw new Error('ExternalActionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ExternalActionViewModelBase implements ExternalActionProps {
  protected readonly _props: ExternalActionProps;
  private _json?: ExternalActionProps;

  get id(): string {
    return this._props.id;
  }
  get entityId(): string {
    return this._props.entityId;
  }
  get status(): ExternalActionStatus {
    return this._props.status;
  }
  get entityType(): EntityType {
    return this._props.entityType;
  }
  get actionTypeId(): string {
    return this._props.actionTypeId;
  }
  get actionBodyJson(): string {
    return this._props.actionBodyJson;
  }
  get completedById(): string | null {
    return this._props.completedById;
  }
  get dateCompleted(): Date | null {
    return this._props.dateCompleted;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCompleted() {
    return this._props.status === ExternalActionStatus.Completed;
  }
  public get isDisregarded() {
    return this._props.status === ExternalActionStatus.Disregarded;
  }
  public get isRequested() {
    return this._props.status === ExternalActionStatus.Requested;
  }
  public get isUnknown() {
    return this._props.status === ExternalActionStatus.Unknown;
  }
  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }

  public isAnyStatus(...externalActionStatus: ExternalActionStatus[]) {
    return externalActionStatus.includes(this._props.status);
  }
  public isAnyEntityType(...entityType: EntityType[]) {
    return entityType.includes(this._props.entityType);
  }

  constructor(props: ExternalActionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        entityId: this._props.entityId,
        status: this._props.status,
        entityType: this._props.entityType,
        actionTypeId: this._props.actionTypeId,
        actionBodyJson: this._props.actionBodyJson,
        completedById: this._props.completedById,
        dateCompleted: this._props.dateCompleted,
        notes: this._props.notes,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllExternalActionEntities = (state: AppState) =>
  state.entityStores.externalAction.entities;

export const selectExternalActionById = (id: string) => (state: AppState) =>
  state.entityStores.externalAction.entities.get(id);

export const selectExternalActionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.externalAction.meta.get(id);

export const selectExternalActionAndMetadataById = (id: string) =>
  createSelector(
    selectExternalActionById(id),
    selectExternalActionMetadataById(id),
    (externalAction, meta) => ({ externalAction, meta }),
  );

export function selectExternalActionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllExternalActionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ExternalActionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectExternalActionEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectExternalActionEntitiesByStatus(status: ExternalActionStatus) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectExternalActionEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

export function selectExternalActionEntitiesByActionTypeId(actionTypeId: string) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.actionTypeId === actionTypeId).valueSeq(),
  );
}

export function selectExternalActionEntitiesByActionBodyJson(actionBodyJson: string) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.actionBodyJson === actionBodyJson).valueSeq(),
  );
}

export function selectExternalActionEntitiesByCompletedById(completedById: string) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.completedById === completedById).valueSeq(),
  );
}

export function selectExternalActionEntitiesByDateCompleted(dateCompleted: Date) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.dateCompleted === dateCompleted).valueSeq(),
  );
}

export function selectExternalActionEntitiesByNotes(notes: string) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectExternalActionEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllExternalActionEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
